import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { VendorType } from '@econtractor/types';
import EToolTip from 'components/others/EToolTip';
import SolarIcon from 'components/icons/SolarIcon';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import VendorRowActions from '../VendorRowActions';
import { useVendors } from '../context/VendorsProvider';

interface VendorsTableRowProps {
    vendor: VendorType;
    fetchVendors: (force?: boolean) => Promise<void>;
    setEditVendorId: (vendorId: number) => void;
    setSubjectsVendorId: (vendorId: number) => void;
    setPersonsVendorId: (vendorId: number) => void;
    onOpenPrimaryContacts: (vendor: VendorType) => void;
}

const websiteHref = (value?: string | null): string | null => {
    const website = String(value || '').trim();
    if (!website) return null;
    if (/^https?:\/\//i.test(website)) return website;
    return null;
};

const VendorsTableRow = ({
    vendor,
    fetchVendors,
    setEditVendorId,
    setSubjectsVendorId,
    setPersonsVendorId,
    onOpenPrimaryContacts,
}: VendorsTableRowProps) => {
    const { t } = useTranslation(['Vendors']);
    const { auth } = useAuth();
    const { showContact, showWebsite } = useVendors();
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');
    const href = websiteHref(vendor.vendorWebsite);
    const website = String(vendor.vendorWebsite || '').trim();

    return (
        <tr>
            <td className="name">
                <div
                    className={canEdit ? 'cursor-pointer' : undefined}
                    onClick={() => {
                        if (canEdit) setEditVendorId(vendor.ID);
                    }}
                >
                    <h6 className="mb-0 text-truncate" title={vendor.vendorName || undefined}>
                        {vendor.vendorName || '-'}
                    </h6>
                    {vendor.vendorNote ? (
                        <small className="text-muted d-block text-truncate" title={vendor.vendorNote}>
                            {vendor.vendorNote}
                        </small>
                    ) : null}
                </div>
            </td>
            {showContact ? (
                <td className="contact-person">
                    {vendor.primaryPersonId ? (
                        <div
                            className="cursor-pointer"
                            title={t('persons.contactsHint')}
                            onClick={() => onOpenPrimaryContacts(vendor)}
                        >
                            <div className="text-truncate" title={vendor.primaryPersonName || undefined}>
                                {vendor.primaryPersonName || '-'}
                            </div>
                            {vendor.primaryPersonPosition ? (
                                <small
                                    className="text-muted d-block text-truncate"
                                    title={vendor.primaryPersonPosition}
                                >
                                    {vendor.primaryPersonPosition}
                                </small>
                            ) : null}
                        </div>
                    ) : (
                        '-'
                    )}
                </td>
            ) : null}
            {showWebsite ? (
                <td className="website text-break">
                    {href ? (
                        <a href={href} target="_blank" rel="noopener noreferrer">
                            {website}
                        </a>
                    ) : (
                        website || '-'
                    )}
                </td>
            ) : null}
            <td className="products">{vendor.productsCount || 0}</td>
            {canEdit && (
                <td className="action">
                    <div className="d-flex align-items-center justify-content-end gap-1">
                        <EToolTip title={t('vendorPopup.subjects')}>
                            <span className="d-inline-block">
                                <Button
                                    className="p-1 py-0"
                                    size="sm"
                                    variant="light-secondary"
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        setSubjectsVendorId(vendor.ID);
                                    }}
                                >
                                    <SolarIcon name="person-vcard" />
                                </Button>
                            </span>
                        </EToolTip>
                        <EToolTip title={t('vendorPopup.persons')}>
                            <span className="d-inline-block">
                                <Button
                                    className="p-1 py-0"
                                    size="sm"
                                    variant="light-secondary"
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        setPersonsVendorId(vendor.ID);
                                    }}
                                >
                                    <SolarIcon name="people" />
                                </Button>
                            </span>
                        </EToolTip>
                        <VendorRowActions
                            vendor={vendor}
                            fetchVendors={fetchVendors}
                            setEditVendorId={setEditVendorId}
                            setSubjectsVendorId={setSubjectsVendorId}
                            setPersonsVendorId={setPersonsVendorId}
                        />
                    </div>
                </td>
            )}
        </tr>
    );
};

export default VendorsTableRow;
